import * as React from 'react';
import { connect } from 'react-redux';

import * as Actions from '../actions';

import AnalysisSetCard from './AnalysisSetCard';
import { CoughAnalysisSet, AnalyseState } from '../interfaces/state';
import Alert, { AlertCategoryEnum } from '../../../misc/components/Alert';
import Loading from '../../../misc/components/loading';
import Modal from '../../../misc/components/modal';
import { CoughAnalysisSetsFixedCommentsDisplay, OverreadStatusIdEnum } from '../../../misc/api/apiTypes';
import { useWebPortalLegacyClient } from '../../../misc/api/WebPortalTokenClient';
import { useCoughMarshalApiClient } from '../../../misc/api/CoughMarshalApiClient';
import { useAuth } from 'react-oidc-context';
import { useSecurityContext } from '../../../Core/SecurityContext';
import { CoughAnalysisSetTypeEnum } from '../../../misc/api/CoughAnalysisSetTypes';
import { PermissionEnum } from '../../../misc/api/ApiPermissionEnum';

export interface AnalysisSetDisplayDialogProps {
    analysisSetsLoaded: boolean;
    analysisSets: Array<CoughAnalysisSetsFixedCommentsDisplay>;
    isAnalysisSetDialogOpen: boolean;
    isAlertVisible: boolean;

    sessionId: string;
    primaryAnalystSetId: string;
    isViewOnly: boolean;
    waveform: string;

    dispatch: any;
}

export const AnalysisSetDisplayDialog = (props: AnalysisSetDisplayDialogProps) => {
    const legacyClient = useWebPortalLegacyClient();
    const coughMarshalClient = useCoughMarshalApiClient();
    const { currentUserPermissions } = useSecurityContext()
    const { user } = useAuth();

    const onClose = () => {
        props.dispatch(Actions.CloseAnalysisSetDialogAsyncAction.create(legacyClient, coughMarshalClient, user));
    }

    const renderCards = () => {
        if (props.analysisSetsLoaded) {
            const primaryAnalysisSet = props.analysisSets.find(a => a.Id === props.primaryAnalystSetId);
            const isPrimaryQa = primaryAnalysisSet.Type.Id.includes("QA");
            if (props.analysisSets.length > 0) {
                return props.analysisSets.map(i => {
                    const isPrimaryAnalysisSet = props.primaryAnalystSetId === i.Id;
                    const revealQcPermission = PermissionEnum.RevealQcLayerDuringCoughAnalysisQa;
                    const hasRevealQcPermission = currentUserPermissions.some(p => p.Id === revealQcPermission)
                    const isQc = i.Type.Id == CoughAnalysisSetTypeEnum.QualityCheck;
                    let isQcAndIsQaOpen = isQc;
                    const qaAnalysisSets = props.analysisSets.filter(x => x.Type.Id.includes(CoughAnalysisSetTypeEnum.AuditQualityAssurance) && x.StatusId != OverreadStatusIdEnum.Accepted && x.StatusId != OverreadStatusIdEnum.Rejected)
                    if (isQc) {
                        isQcAndIsQaOpen = qaAnalysisSets.length > 0;
                    }
                    const isBlinded = isPrimaryQa && isQcAndIsQaOpen && !hasRevealQcPermission;
                    return (<AnalysisSetCard
                        key={i.Id}
                        id={i.Id}
                        type={i.Type.Name}
                        disabled={isPrimaryAnalysisSet || isBlinded}
                        isCurrentSet={isPrimaryAnalysisSet}
                        isBlinded={isBlinded}
                        createdDateTime={i.CreatedDateTime}
                        analyst={i.Analyst.Username}
                        acceptability={i.StatusId}
                        checked={i.IsSelected}
                        onAnalysisSetSelect={checked => props.dispatch(Actions.ToggleAnalysisSetAction.create(i.Id, checked))} />);
                });
            } else {
                return (
                    <Alert show={true} category={AlertCategoryEnum.Danger}>
                        There are no analysis sets available
                    </Alert>
                );
            }
        } else {
            return <Loading />;
        }
    }

    const renderValidation = () => {
        if (props.isAlertVisible) {
            return (
                <Alert show={true} category={AlertCategoryEnum.Warning}>
                    A maximum of 5 analysis sets can be selected at one time.
                </Alert>
            );
        }
    }

    return (
        <Modal show={props.isAnalysisSetDialogOpen} header="Active Analysis Sets" closeModalHandler={() => onClose()}>
            <div className="modal-body">
                {renderValidation()}
                <div className="row">
                    <div className="col-md-12">
                        <ul className="posh-select">
                            {renderCards()}
                        </ul>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => onClose()}>OK</button>
            </div>
        </Modal>
    );
}

function mapStateToProps(state: AnalyseState) {
    return {
        primaryAnalystSetId: state.analysisSet.primaryAnalystSetId,
        analysisSetsLoaded: state.analysisSet.coughAnalysisSets.length > 0,
        analysisSets: state.analysisSet.coughAnalysisSets,
        isAnalysisSetDialogOpen: state.analysisSet.isAnalysisSetDialogOpen,
        isAlertVisible: state.analysisSet.isMaximumAnalysisSetsAlertVisible,
        sessionId: state.core.sessionMetadata.sessionId,
        isViewOnly: state.core.isViewOnly,
        waveform: state.core.selectedWaveform
    } as AnalysisSetDisplayDialogProps;
}

export default connect<AnalysisSetDisplayDialogProps, any, void>(mapStateToProps)(AnalysisSetDisplayDialog);
